/**
 * External dependencies
 */
import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';
import { SettingsControlProps } from './types';

const MinimalSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: '24px !important',
    fontWeight: 500,
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center'
  },
  '&:before': { borderBottom: 'none !important' },
  '&:after': { borderBottom: 'none !important' },
  '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
  '& .MuiSvgIcon-root': {
    right: -4,
    color: theme.palette.text.secondary,
    fontSize: '1.2rem',
    position: 'absolute'
  }
}));

function SettingsControl(props: SettingsControlProps): React.ReactElement {
  /* Global & Local State */
  const {
    sx,
    lang,
    scale,
    languageOptions,
    sizeOptions,
    sizeLabel = 'Size',
    fullWidth = false,
    variant = 'default',
    onLangChange,
    onScaleChange
  } = props;

  const [leftLang, rightLang] = languageOptions;
  const isFlat = variant === 'flat';

  /* Functions */
  const handleSwitchToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newLang = event.target.checked ? rightLang.value : leftLang.value;
    onLangChange(newLang);
  };
  const handleSizeChange = (event: SelectChangeEvent<unknown>): void => {
    onScaleChange(Number(event.target.value));
  };
  const handleLabelClick = (targetLang: string): void => {
    if (lang !== targetLang) {
      onLangChange(targetLang);
    }
  };

  /* Main */
  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'auto',
        ...sx
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: fullWidth ? 'flex' : 'inline-flex',
          width: fullWidth ? '100%' : 'auto',
          alignItems: 'center',
          height: 48,
          px: isFlat ? 0 : 3,
          justifyContent: fullWidth || isFlat ? 'space-between' : 'flex-start',
          borderRadius: isFlat ? 0 : '50px',
          boxShadow: isFlat ? 'none' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
          backgroundColor: isFlat ? 'transparent' : '#fff',
          border: 'none'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: fullWidth && !isFlat ? 1 : undefined,
            justifyContent: fullWidth && !isFlat ? 'center' : 'flex-start'
          }}
        >
          <Typography
            onClick={() => handleLabelClick(leftLang.value)}
            sx={{
              fontSize: '0.9rem',
              color: lang === leftLang.value ? 'text.primary' : 'text.secondary',
              fontWeight: lang === leftLang.value ? 500 : 400,
              cursor: 'pointer',
              transition: 'color 0.3s'
            }}
          >
            {leftLang.label}
          </Typography>
          <Switch
            checked={lang === rightLang.value}
            onChange={handleSwitchToggle}
            inputProps={{ 'aria-label': 'language switch' }}
          />
          <Typography
            onClick={() => handleLabelClick(rightLang.value)}
            sx={{
              fontSize: '0.9rem',
              color: lang === rightLang.value ? 'text.primary' : 'text.secondary',
              fontWeight: lang === rightLang.value ? 500 : 400,
              cursor: 'pointer',
              transition: 'color 0.3s'
            }}
          >
            {rightLang.label}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 2.5, my: 3.5, borderColor: '#E0E0E0' }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: fullWidth && !isFlat ? 1 : undefined,
            justifyContent: fullWidth && !isFlat ? 'center' : 'flex-end'
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', color: '#9E9E9E' }}>{sizeLabel}</Typography>
          <MinimalSelect
            value={scale}
            onChange={handleSizeChange}
            variant="standard"
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  borderRadius: 2,
                  mt: 1,
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                }
              }
            }}
          >
            {sizeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </MinimalSelect>
        </Box>
      </Paper>
    </Box>
  );
}

export default memo(SettingsControl);
